import { motion } from "framer-motion";
import { Calendar, Phone } from "lucide-react";
import heroImage from "@/assets/hero-diner-tv.webp";
import heroImageMd from "@/assets/hero-diner-tv-md.webp";
import CalEmbed from "./CalEmbed";

const CAL_LINK = "https://cal.com/tabletalktv";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: [0, 0, 0.2, 1] as const },
  }),
};

const Hero = () => {
  return (
    <section className="relative min-h-svh flex items-center overflow-hidden pt-20" id="hero">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          srcSet={`${heroImageMd} 1280w, ${heroImage} 1920w`}
          sizes="100vw"
          alt="Modern diner with 60-inch TV screen showing commercial advertisement while families enjoy meals"
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/40" />
      </div>


      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 md:py-0">
        <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center">
          <div className="max-w-2xl">
          <motion.p
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="uppercase tracking-[0.2em] text-brand text-xs font-bold mb-6"
          >
            Local TV Advertising · Nassau & Suffolk County, NY
          </motion.p>

          <motion.h1
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6 text-[var(--color-text)]"
          >
            Get Your Business In Front of Thousands of
            <br />
            <span className="text-brand">Local Customers</span>
            <br />
            Every Single Month.
          </motion.h1>

          <motion.p
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-muted-custom text-base sm:text-lg max-w-xl mb-8 leading-relaxed"
          >
            For contractors, restaurants, and home-service pros tired of rising Facebook CPMs and shrinking mailer ROI. Your 30-second ad runs on 60" HD screens inside Long Island's busiest diners, where locals sit for 30–45 minutes and actually look up. <strong className="text-[var(--color-text)]">From less than $10/day.</strong>
          </motion.p>

          <motion.div
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex flex-col sm:flex-row gap-3 mb-8"
          >
            {/* Primary CTA: Book a Call via Cal.com */}
            <a
              href={CAL_LINK}
              className="btn-primary px-8 py-4 text-base"
            >
              <Calendar className="w-5 h-5" />
              Book a Call
            </a>
            {/* Call Now */}
            <a
              href="tel:+15163433353"
              className="btn-secondary px-8 py-4 text-base"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </motion.div>

          <motion.div
            custom={4}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap gap-3"
          >
            {["30,000+ monthly diners", "30–45 min avg. dwell time", "60\" HD screens", "No monthly fees — 12-mo flat rate"].map(
              (item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-custom bg-white/80 backdrop-blur-sm border border-[var(--color-border)] rounded-full px-3 py-1.5 transition-all duration-300 cursor-default hover:border-yellow-400 hover:text-yellow-600 hover:shadow-[0_0_18px_rgba(250,204,21,0.7)] hover:bg-yellow-50"
                >
                  <span className="text-brand">✓</span> {item}
                </span>
              )
            )}
          </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="w-full lg:w-[420px]"
          >
            <CalEmbed />
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default Hero;
